import React, { useState, useEffect } from 'react';
import { Sparkles, Copy, Check, Quote as QuoteIcon, RefreshCw, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import PovDialog from './PovDialog';

/**
 * PovPostsTab
 * -----------
 * Dedicated home for Point-of-View posts. Shows the history of POV posts
 * generated for this analysis with a primary "Generate POV" CTA.
 *
 * POV data shape (single item):
 *   {
 *     headline_hook: string,
 *     post_text: string,
 *     stance: string,
 *     citations: [{ title, url }],
 *     topic: string,
 *     source_url: string,
 *     created_at: string,
 *   }
 */

function PovCard({ pov, index, testidPrefix = 'pov-card' }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(index === 0); // newest expanded by default

  const handleCopy = async () => {
    if (!pov?.post_text) return;
    try {
      await navigator.clipboard.writeText(pov.post_text);
      setCopied(true);
      toast.success('POV post copied');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy');
    }
  };

  const citations = pov?.citations || [];
  const created = pov?.created_at ? new Date(pov.created_at) : null;

  return (
    <Card
      className="relative border-l-4 border-[hsl(var(--accent))] bg-gradient-to-br from-[hsl(var(--accent))]/[0.08] via-card to-emerald-500/[0.04] shadow-[0_10px_28px_-20px_rgba(0,0,0,0.35)] hover:shadow-[0_18px_44px_-22px_rgba(16,185,129,0.35)] transition-shadow overflow-hidden"
      data-testid={`${testidPrefix}-${index}`}
    >
      <CardContent className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] shrink-0 shadow-sm">
            <QuoteIcon size={15} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="font-mono-edge text-[10px] tracking-[0.16em] bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] border border-[hsl(var(--accent))]/30">
                POV #{index + 1}
              </Badge>
              {pov?.topic && (
                <Badge variant="outline" className="text-[10px]">
                  {pov.topic}
                </Badge>
              )}
              {created && (
                <span className="text-[11px] text-muted-foreground">
                  {created.toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </span>
              )}
            </div>
            <h3 className="font-semibold text-base mt-1.5 leading-snug" data-testid={`${testidPrefix}-${index}-hook`}>
              {pov?.headline_hook || pov?.stance || 'Untitled POV'}
            </h3>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCopy}
              data-testid={`${testidPrefix}-${index}-copy`}
            >
              {copied ? (
                <Check size={14} className="text-[hsl(var(--success))]" />
              ) : (
                <Copy size={14} />
              )}
            </Button>
          </div>
        </div>

        <div
          className={`relative ${expanded ? '' : 'max-h-32 overflow-hidden'}`}
          data-testid={`${testidPrefix}-${index}-body`}
        >
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground/90">
            {pov?.post_text || ''}
          </p>
          {!expanded && (
            <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-card to-transparent" />
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="mt-2 text-primary hover:text-primary"
          onClick={() => setExpanded((v) => !v)}
          data-testid={`${testidPrefix}-${index}-expand`}
        >
          {expanded ? 'Show less' : 'Show more'}
        </Button>

        {citations.length > 0 && (
          <div className="mt-4 border-t border-border pt-3">
            <p className="font-mono-edge text-[10px] tracking-[0.18em] text-muted-foreground mb-2">
              CITATIONS · {citations.length}
            </p>
            <ul className="space-y-1.5">
              {citations.slice(0, 5).map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span className="text-muted-foreground font-mono-edge shrink-0">[{i + 1}]</span>
                  {c.url ? (
                    <a
                      href={c.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline inline-flex items-start gap-1 min-w-0"
                      data-testid={`${testidPrefix}-${index}-citation-${i}`}
                    >
                      <span className="truncate">{c.title || c.url}</span>
                      <ExternalLink size={11} className="shrink-0 mt-0.5" />
                    </a>
                  ) : (
                    <span>{c.title}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function PovPostsTab({ data, locked, lockReason, onUpgrade, defaultCountry, defaultLengthMode, seedTheme, onSeedThemeConsumed }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const history = data?.pov_history || [];
  const latest = data?.latest_pov;

  // If a Presence-Plan theme was handed off from the audit, auto-open
  // the POV composer with the theme prefilled as an angle hint so the
  // user lands on the dialog ready to pick a supporting news article.
  useEffect(() => {
    if (seedTheme) {
      setDialogOpen(true);
    }
  }, [seedTheme]);

  // Merge latest into history if not already there (defensive)
  const allPovs = (() => {
    if (!latest) return history;
    const found = history.find((h) => h.post_text === latest.post_text);
    if (found) return history;
    return [latest, ...history];
  })();

  const handlePovGenerated = () => {
    // Triggers parent refresh via the same pattern used by other tabs - the
    // analysis polling on AnalysisPage already refreshes the analysis doc.
    toast.success('POV added to your history');
  };

  return (
    <div className="space-y-5" data-testid="pov-tab-root">
      {/* Hero / CTA strip */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground shrink-0">
              <Sparkles size={16} />
            </span>
            <div className="min-w-0">
              <p className="font-mono-edge text-[10px] tracking-[0.18em] text-primary">POINT OF VIEW POSTS</p>
              <h2 className="font-display text-xl tracking-[-0.01em] mt-0.5">
                Land a sharp opinion on a trending story.
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Pick a trending headline or paste any article URL &mdash; we&apos;ll write a voice-matched POV
                with inline citations to verifiable sources.
              </p>
            </div>
          </div>
          <Button
            onClick={() => (locked ? onUpgrade?.() : setDialogOpen(true))}
            className="shrink-0"
            data-testid="pov-tab-generate-cta"
          >
            <Sparkles size={14} className="mr-2" />
            {locked ? 'Upgrade to generate' : allPovs.length > 0 ? 'Generate new POV' : 'Generate first POV'}
          </Button>
        </CardContent>
      </Card>

      {locked && (
        <Card className="border-[hsl(var(--warning))]/30 bg-[hsl(var(--warning))]/8" data-testid="pov-tab-lock-notice">
          <CardContent className="p-4 flex items-start gap-3">
            <RefreshCw size={16} className="text-[hsl(var(--warning))] mt-0.5 shrink-0" />
            <div className="min-w-0">
              <p className="font-medium text-sm">{lockReason || 'POV posts require an active plan.'}</p>
              <p className="text-xs text-muted-foreground mt-1">
                The Executive Thought Leadership Plan unlocks unlimited POV generation with real-time research and citations.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* History list */}
      {allPovs.length === 0 ? (
        <Card className="border-dashed border-border" data-testid="pov-tab-empty">
          <CardContent className="p-10 text-center">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
              <QuoteIcon size={20} />
            </div>
            <h3 className="font-display text-lg">No POV posts yet</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">
              POV posts let you stake a position on a hot story in your industry. Click <strong>Generate POV</strong> to
              create your first one &mdash; each draft comes with verifiable citations you can paste straight to LinkedIn.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-mono-edge text-[10px] tracking-[0.18em] text-muted-foreground">
              POV HISTORY &middot; {allPovs.length} {allPovs.length === 1 ? 'POST' : 'POSTS'}
            </p>
          </div>
          {allPovs.map((pov, i) => (
            <PovCard key={i} pov={pov} index={i} />
          ))}
        </div>
      )}

      <PovDialog
        open={dialogOpen}
        onClose={() => { setDialogOpen(false); onSeedThemeConsumed?.(); }}
        analysisId={data?.id}
        onPovGenerated={handlePovGenerated}
        defaultCountry={defaultCountry}
        defaultLengthMode={defaultLengthMode}
        seedTheme={seedTheme}
      />
    </div>
  );
}
